import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, EmptyState } from "@/components/States";
import { Heart, MessageCircle, MoreVertical, Flag, Trash2, Send, Plus, ImagePlus, Users, X, Tag, Pencil } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "feed", label: "Feed" },
  { key: "grupos", label: "Grupos" },
  { key: "classificados", label: "Classificados" },
];

const CLASSIFIED_CATS = ["Todos", "Vendo", "Compro", "Doação", "Serviços", "Imóveis", "Outros"];

function useUpload() {
  return async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };
}

const idLine = (a) => [a?.unit ? `Apt. ${a.unit}` : null, a?.tower].filter(Boolean).join(" • ") || "Morador";

function Avatar({ name, url, size = 40 }) {
  const initials = (name || "M").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return url ? (
    <img src={fileUrl(url)} alt={name} className="rounded-full object-cover" style={{ width: size, height: size }} />
  ) : (
    <div className="rounded-full brand-gradient flex items-center justify-center text-white font-semibold" style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {initials}
    </div>
  );
}

function PostCard({ post, onChange }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showComments, setShowComments] = useState(false);
  const [comment, setComment] = useState("");
  const [menu, setMenu] = useState(false);

  const { data: comments } = useQuery({
    queryKey: ["comments", post.id], enabled: showComments,
    queryFn: async () => (await api.get(`/posts/${post.id}/comments`)).data,
  });

  const like = async () => {
    try { await api.post(`/posts/${post.id}/like`); onChange(); } catch (e) { toast.error(apiError(e)); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/posts/${post.id}/comments`, { text: comment });
      setComment(""); qc.invalidateQueries({ queryKey: ["comments", post.id] }); onChange();
    } catch (e) { toast.error(apiError(e)); }
  };
  const report = async () => { try { await api.post(`/posts/${post.id}/report`); toast.success("Publicação denunciada."); setMenu(false); } catch (e) { toast.error(apiError(e)); } };
  const del = async () => { try { await api.delete(`/posts/${post.id}`); toast.success("Publicação removida."); onChange(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div data-testid="post-card" className="glass rounded-3xl p-4 card-hover">
      <div className="flex items-center gap-3">
        <Avatar name={post.author?.name} url={post.author?.avatar_url} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{post.author?.name}</p>
          <p className="text-xs text-muted-foreground">{idLine(post.author)}{post.pinned && " · 📌 fixado"}</p>
        </div>
        <div className="relative">
          <button onClick={() => setMenu(!menu)} data-testid="post-menu" className="p-1.5 rounded-full hover:bg-white/10 transition-colors"><MoreVertical className="w-4 h-4" /></button>
          {menu && (
            <div className="absolute right-0 top-9 z-10 w-40 glass-strong rounded-xl border border-white/10 py-1">
              <button onClick={report} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/10 transition-colors"><Flag className="w-4 h-4" /> Denunciar</button>
              {(post.author?.id === user.id) && (
                <button onClick={del} data-testid="post-delete" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/10 transition-colors"><Trash2 className="w-4 h-4" /> Excluir</button>
              )}
            </div>
          )}
        </div>
      </div>
      <p className="text-sm mt-3 whitespace-pre-wrap">{post.text}</p>
      {post.photo_url && <img src={fileUrl(post.photo_url)} alt="" className="mt-3 rounded-2xl w-full object-cover max-h-80" />}
      <div className="flex items-center gap-5 mt-3 pt-3 border-t border-white/5">
        <button onClick={like} data-testid="post-like" className={`flex items-center gap-1.5 text-sm transition-colors ${post.liked ? "text-pink-400" : "text-muted-foreground hover:text-foreground"}`}>
          <Heart className={`w-4 h-4 ${post.liked ? "fill-pink-400" : ""}`} /> {post.likes_count}
        </button>
        <button onClick={() => setShowComments(!showComments)} data-testid="post-comment-toggle" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <MessageCircle className="w-4 h-4" /> {post.comments_count}
        </button>
      </div>
      {showComments && (
        <div className="mt-3 space-y-3">
          {comments?.map((c) => (
            <div key={c.id} className="flex gap-2">
              <Avatar name={c.author?.name} url={c.author?.avatar_url} size={30} />
              <div className="flex-1 rounded-2xl bg-white/5 px-3 py-2">
                <p className="text-xs font-semibold">{c.author?.name}</p>
                <p className="text-sm">{c.text}</p>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} data-testid="comment-input" placeholder="Escreva um comentário..." className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-sm outline-none focus:border-primary/50 transition-colors" />
            <button onClick={addComment} data-testid="comment-send" className="p-2 rounded-full brand-gradient text-white active:scale-90 transition-transform"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function NewPost({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const upload = useUpload();

  const submit = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      let photo_url = null;
      if (photo) photo_url = await upload(photo);
      await api.post("/posts", { text, photo_url });
      setText(""); setPhoto(null); setOpen(false); onCreated();
      toast.success("Publicado na comunidade!");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  if (!open)
    return (
      <button onClick={() => setOpen(true)} data-testid="new-post-btn" className="w-full glass rounded-3xl p-4 text-left text-sm text-muted-foreground hover:border-primary/40 border border-transparent transition-colors flex items-center gap-3">
        <Plus className="w-5 h-5 text-primary" /> Compartilhe algo com a comunidade...
      </button>
    );

  return (
    <div className="glass rounded-3xl p-4 space-y-3">
      <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="new-post-text" rows={3} autoFocus placeholder="No que você está pensando?" className="w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-primary/50 transition-colors resize-none" />
      {photo && <p className="text-xs text-primary">📎 {photo.name}</p>}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
          <ImagePlus className="w-4 h-4" /> Foto
          <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files[0])} />
        </label>
        <div className="flex gap-2">
          <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm hover:bg-white/10 transition-colors">Cancelar</button>
          <button onClick={submit} disabled={loading} data-testid="new-post-submit" className="px-5 py-2 rounded-full brand-gradient text-white text-sm font-semibold active:scale-95 transition-transform disabled:opacity-60">{loading ? "..." : "Publicar"}</button>
        </div>
      </div>
    </div>
  );
}

function Feed() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["posts"], queryFn: async () => (await api.get("/posts")).data });
  return (
    <div className="space-y-4">
      <NewPost onCreated={refetch} />
      {isLoading ? <Loading /> : data?.length === 0 ? <EmptyState icon={Users} title="Comunidade vazia" subtitle="Seja o primeiro a publicar algo por aqui." /> :
        data.map((p) => <PostCard key={p.id} post={p} onChange={refetch} />)}
    </div>
  );
}

function GroupRoom({ group, onClose }) {
  const [text, setText] = useState("");
  const { data: members } = useQuery({ queryKey: ["group-members", group.id], queryFn: async () => (await api.get(`/groups/${group.id}/members`)).data });
  const { data: posts, refetch } = useQuery({ queryKey: ["group-posts", group.id], queryFn: async () => (await api.get("/posts", { params: { group_id: group.id } })).data });
  const send = async () => {
    if (!text.trim()) return;
    try { await api.post("/posts", { text, group_id: group.id }); setText(""); refetch(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full sm:max-w-md glass-strong rounded-t-3xl sm:rounded-3xl border border-white/10 float-in pb-safe h-[88dvh] sm:h-[640px] flex flex-col" data-testid="group-room">
        <div className="flex items-center gap-3 p-4 border-b border-white/10">
          <span className="text-2xl">{group.icon}</span>
          <div className="flex-1"><p className="font-display font-bold text-sm">{group.name}</p><p className="text-xs text-muted-foreground">{members?.length || group.member_count} participantes</p></div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1.5 px-4 py-2 overflow-x-auto border-b border-white/5">
          {members?.map((m) => (
            <span key={m.id} className="shrink-0 text-[11px] px-2 py-1 rounded-full bg-white/5 border border-white/10">{m.role === "manager" ? "🛡️ " : ""}{m.name.split(" ")[0]}{m.unit ? ` · ${m.unit}` : ""}</span>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {posts?.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Nenhuma conversa ainda. Comece você!</p>}
          {posts?.map((p) => <PostCard key={p.id} post={p} onChange={refetch} />)}
        </div>
        <div className="p-3 border-t border-white/10 flex items-center gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="group-post-input" placeholder="Escreva no grupo..." className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50" />
          <button onClick={send} data-testid="group-post-send" className="p-2.5 rounded-full brand-gradient text-white active:scale-90 transition-transform"><Send className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

function Groups() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["groups"], queryFn: async () => (await api.get("/groups")).data });
  const [open, setOpen] = useState(false);
  const [room, setRoom] = useState(null);
  const [form, setForm] = useState({ name: "", icon: "👥", description: "" });

  const join = async (id) => { try { await api.post(`/groups/${id}/join`); refetch(); } catch (e) { toast.error(apiError(e)); } };
  const create = async () => {
    if (!form.name.trim()) return;
    try { await api.post("/groups", form); setOpen(false); setForm({ name: "", icon: "👥", description: "" }); refetch(); toast.success("Grupo criado!"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (isLoading) return <Loading />;
  return (
    <div className="space-y-4">
      <button onClick={() => setOpen(true)} data-testid="create-group-btn" className="w-full rounded-3xl brand-gradient p-4 text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
        <Plus className="w-5 h-5" /> Criar grupo
      </button>
      <div className="grid grid-cols-2 gap-3">
        {data.map((g) => (
          <div key={g.id} data-testid="group-card" onClick={() => setRoom(g)} className="glass rounded-3xl p-4 card-hover cursor-pointer">
            <div className="text-3xl mb-2">{g.icon}</div>
            <p className="font-display font-semibold text-sm">{g.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{g.description}</p>
            <p className="text-xs text-muted-foreground mt-2">{g.member_count} membros</p>
            <button onClick={(e) => { e.stopPropagation(); join(g.id); }} data-testid="group-join" className={`mt-3 w-full rounded-full py-2 text-xs font-semibold transition-colors ${g.joined ? "bg-white/5 border border-white/10" : "brand-gradient text-white"}`}>
              {g.joined ? "Participando" : "Participar"}
            </button>
          </div>
        ))}
      </div>

      {room && <GroupRoom group={room} onClose={() => { setRoom(null); refetch(); }} />}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-sm glass-strong rounded-3xl border border-white/10 p-6 float-in">
            <div className="flex items-center justify-between mb-4"><p className="font-display font-bold">Criar grupo</p><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              <input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} data-testid="group-icon" placeholder="Emoji (ex: 🎾)" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50" />
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name" placeholder="Nome do grupo" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50" />
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="group-desc" rows={2} placeholder="Descrição" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50 resize-none" />
              <button onClick={create} data-testid="group-create-submit" className="w-full rounded-full brand-gradient py-3 text-white font-semibold active:scale-95 transition-transform">Criar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Classifieds() {
  const { user } = useAuth();
  const [cat, setCat] = useState("Todos");
  const [detail, setDetail] = useState(null);
  const { data, isLoading, refetch } = useQuery({ queryKey: ["classifieds", cat], queryFn: async () => (await api.get("/classifieds", { params: { category: cat } })).data });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category: "Vendo", price: "", whatsapp: "" });
  const [photo, setPhoto] = useState(null);
  const [existingPhoto, setExistingPhoto] = useState(null);

  const del = async (id) => {
    try { await api.delete(`/classifieds/${id}`); setDetail(null); refetch(); toast.success("Anúncio excluído."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const startEdit = (it) => {
    setForm({ title: it.title, description: it.description, category: it.category, price: it.price || "", whatsapp: it.whatsapp || "" });
    setEditingId(it.id); setPhoto(null); setExistingPhoto(it.photo_url || null); setDetail(null); setOpen(true);
  };

  const create = async () => {
    if (!form.title.trim()) return;
    try {
      let photoUrl = existingPhoto;
      if (photo) { const fd = new FormData(); fd.append("file", photo); const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); photoUrl = up.path; }
      const payload = { ...form, photo_url: photoUrl || null, photos: photoUrl ? [photoUrl] : [] };
      if (editingId) await api.put(`/classifieds/${editingId}`, payload);
      else await api.post("/classifieds", payload);
      setOpen(false); setEditingId(null); setForm({ title: "", description: "", category: "Vendo", price: "", whatsapp: "" }); setPhoto(null); setExistingPhoto(null); refetch();
      toast.success(editingId ? "Anúncio atualizado!" : "Anúncio publicado!");
    }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {CLASSIFIED_CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid="classified-cat" className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${cat === c ? "brand-gradient text-white" : "bg-white/5 border border-white/10 text-muted-foreground"}`}>{c}</button>
        ))}
      </div>
      <button onClick={() => { setEditingId(null); setForm({ title: "", description: "", category: "Vendo", price: "", whatsapp: "" }); setPhoto(null); setExistingPhoto(null); setOpen(true); }} data-testid="new-classified-btn" className="w-full rounded-3xl brand-gradient p-4 text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform"><Plus className="w-5 h-5" /> Novo anúncio</button>
      {isLoading ? <Loading /> : data?.length === 0 ? <EmptyState icon={Tag} title="Sem anúncios" subtitle="Publique o primeiro classificado." /> : (
        <div className="grid gap-3">
          {data.map((it) => (
            <button key={it.id} data-testid="classified-card" onClick={() => setDetail(it)} className="w-full text-left glass rounded-3xl p-4 card-hover flex gap-3">
              {it.photo_url && <img src={fileUrl(it.photo_url)} alt="" className="w-16 h-16 rounded-2xl object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-primary font-medium">{it.category}</span>
                  {it.price && <span className="text-sm font-bold text-emerald-400">{it.price}</span>}
                </div>
                <p className="font-display font-semibold text-sm mt-1.5">{it.title}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{it.description}</p>
                <p className="text-xs text-muted-foreground mt-2">{it.author?.name} · {idLine(it.author)}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDetail(null)} />
          <div className="relative w-full sm:max-w-md glass-strong rounded-t-3xl sm:rounded-3xl border border-white/10 p-6 float-in pb-safe max-h-[88dvh] overflow-y-auto" data-testid="classified-detail">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] px-2.5 py-1 rounded-full bg-primary/15 text-primary font-medium">{detail.category}</span>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5" /></button>
            </div>
            {detail.photo_url && <img src={fileUrl(detail.photo_url)} alt="" className="w-full rounded-2xl object-cover max-h-64 mb-3" />}
            <p className="font-display font-bold text-lg">{detail.title}</p>
            {detail.price && <p className="text-emerald-400 font-bold mt-1">{detail.price}</p>}
            <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{detail.description}</p>
            <div className="mt-4 rounded-2xl bg-white/5 p-3">
              <p className="text-xs text-muted-foreground">Anunciante</p>
              <p className="text-sm font-semibold">{detail.author?.name}</p>
              <p className="text-xs text-muted-foreground">{idLine(detail.author)}</p>
            </div>
            {detail.whatsapp ? (
              <a href={`https://wa.me/55${detail.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" data-testid="classified-whatsapp-btn" className="mt-4 w-full flex items-center justify-center gap-2 rounded-full brand-gradient py-3 text-white font-semibold active:scale-95 transition-transform">
                Conversar pelo WhatsApp
              </a>
            ) : (
              <p className="text-[11px] text-muted-foreground mt-3">O anunciante não informou WhatsApp. Dados pessoais não são exibidos automaticamente.</p>
            )}
            {detail.author_id === user?.id && (
              <div className="mt-4 flex gap-2" data-testid="classified-owner-actions">
                <button onClick={() => startEdit(detail)} data-testid="classified-edit-btn" className="flex-1 flex items-center justify-center gap-2 rounded-full bg-white/5 border border-white/10 py-2.5 text-sm font-medium active:scale-95 transition-transform"><Pencil className="w-4 h-4" /> Editar</button>
                <button onClick={() => del(detail.id)} data-testid="classified-delete-btn" className="flex-1 flex items-center justify-center gap-2 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 py-2.5 text-sm font-medium active:scale-95 transition-transform"><Trash2 className="w-4 h-4" /> Excluir</button>
              </div>
            )}
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5">
          <div className="absolute inset-0 bg-black/70" onClick={() => { setOpen(false); setEditingId(null); }} />
          <div className="relative w-full max-w-sm glass-strong rounded-3xl border border-white/10 p-6 float-in">
            <div className="flex items-center justify-between mb-4"><p className="font-display font-bold">{editingId ? "Editar anúncio" : "Novo anúncio"}</p><button onClick={() => { setOpen(false); setEditingId(null); }}><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="classified-category" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50">
                {CLASSIFIED_CATS.filter((c) => c !== "Todos").map((c) => <option key={c} value={c} className="bg-background">{c}</option>)}
              </select>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="classified-title" placeholder="Título" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50" />
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="classified-desc" placeholder="Descrição" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50 resize-none" />
              <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="classified-price" placeholder="Valor (opcional, ex: R$ 200)" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50" />
              <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="classified-whatsapp" placeholder="WhatsApp para contato (ex: 11999998888)" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50" />
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer"><ImagePlus className="w-4 h-4" /> {photo ? photo.name : "Adicionar foto (opcional)"}<input type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files[0])} data-testid="classified-photo" /></label>
              <button onClick={create} data-testid="classified-submit" className="w-full rounded-full brand-gradient py-3 text-white font-semibold active:scale-95 transition-transform">{editingId ? "Salvar alterações" : "Publicar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Community() {
  const [tab, setTab] = useState("feed");
  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight">Comunidade</h1>
        <p className="text-sm text-muted-foreground">A rede privada do seu condomínio.</p>
      </div>
      <div className="flex gap-2 glass rounded-full p-1">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`community-tab-${t.key}`} className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${tab === t.key ? "brand-gradient text-white" : "text-muted-foreground"}`}>{t.label}</button>
        ))}
      </div>
      {tab === "feed" && <Feed />}
      {tab === "grupos" && <Groups />}
      {tab === "classificados" && <Classifieds />}
    </div>
  );
}
